import { useEffect, useState } from "react";

import DropdownWithSearch from "../../atomic/DropdownWithSearch";
import GetAuthContext from "../../../../../src/shared/context/AuthContext";
import { LENDER_IDs } from "../../../shared/utils/Global";

interface IApplicant {
  name: string;
  value: string;
}

const ApplicantSelectionDropdown = ({
  loanDetails,
  onChange,
  selectedApplicant,
  setSelectedApplicant,
  setApplicantData: setApplicantDataParent,
  hasSearch = false,
  styles = {
    container: "!w-44"
  },
  resetApplicantState,
  availableApplicants,
  placeholder,
  truncateName = true,
  studioExperience = false,
  disabled = false,
  callDefaultAPI = false,
  isLeadsPage = false,
  coApplicantDetails,
  userID
}: {
  loanDetails?: {
    name?: string;
    mobile?: string;
    userID?: string;
    firmName?: string;
    coApplicantDetails?: Array<{
      name?: string;
      mobile?: string;
      firmName?: string;
      userID?: string;
    }>;
  };
  isLeadsPage?: boolean;
  coApplicantDetails: any;
  onChange: (applicant: IApplicant) => void;
  selectedApplicant: IApplicant | null;
  setSelectedApplicant: (applicant: IApplicant) => void;
  setApplicantData?: (applicant: IApplicant[]) => void;
  hasSearch?: boolean;
  styles?: {
    container?: string;
  };
  resetApplicantState: boolean;
  availableApplicants?: IApplicant[];
  placeholder?: string;
  truncateName: boolean;
  studioExperience?: boolean;
  disabled?: boolean;
  callDefaultAPI?: boolean;
  userID?: string;
}) => {
  const [applicantData, setApplicantData] = useState<IApplicant[]>([]);
  const [computedWidthPx, setComputedWidthPx] = useState<number>(360);

  const { userState } = GetAuthContext();

  const isABFLCO_Lending =
    userState?.userData?.lenderID === LENDER_IDs.ABFL_CO_LENDING;

  // Helper function to truncate long names only when necessary
  const truncateNameFunc = (name: string, maxLength: number = 35): string => {
    if (name.length <= maxLength || !truncateName) return name;

    return name.substring(0, maxLength) + "...";
  };

  useEffect(() => {
    const getPrimaryDisplayName = () =>
      isABFLCO_Lending
        ? loanDetails?.name || loanDetails?.firmName
        : (loanDetails?.name ?? "Applicant");

    const primaryDisplayName = getPrimaryDisplayName();
    const primaryApplicant: IApplicant = {
      name: truncateNameFunc(
        `${primaryDisplayName && loanDetails?.mobile ? `${primaryDisplayName} - ${loanDetails?.mobile}` : primaryDisplayName || loanDetails?.mobile || "Unknown"} (Primary)`
      ),
      value: isLeadsPage ? userID! : loanDetails?.userID || ""
    };

    const applicantDataArr: IApplicant[] = [primaryApplicant];
    const coApplicants =
      isLeadsPage && !loanDetails
        ? coApplicantDetails
        : loanDetails?.coApplicantDetails;

    if (coApplicants?.length) {
      for (let index = 0; index < coApplicants.length; index++) {
        const coApplicant = coApplicants[index];

        // Determine display name for co-applicant
        const getCoApplicantDisplayName = () =>
          isABFLCO_Lending
            ? coApplicant.name || coApplicant.firmName
            : coApplicant.name;

        const coApplicantDisplayName = getCoApplicantDisplayName();
        applicantDataArr.push({
          name: truncateNameFunc(
            `${coApplicantDisplayName && coApplicant?.mobile ? `${coApplicantDisplayName} - ${coApplicant?.mobile}` : coApplicantDisplayName || coApplicant?.mobile || coApplicant?.firmName || "Unknown"} (Co-Applicant)`
          ),
          value: coApplicant.userID || ""
        });
      }
    }

    // Use filtered applicants if provided, otherwise use all applicants
    const finalApplicantData = availableApplicants || applicantDataArr;
    setApplicantData(finalApplicantData);

    if (setApplicantDataParent) setApplicantDataParent(finalApplicantData);

    // Only set selected applicant if:
    // 1. No current selection exists    // 2. Available options exist
    // 3. We're not using filtered options (to avoid auto-selecting when options are limited)
    if (
      !selectedApplicant &&
      finalApplicantData.length > 0 && // && availableApplicants === undefined
      !studioExperience
    ) {
      setSelectedApplicant(finalApplicantData[0]);

      callDefaultAPI && onChange?.(finalApplicantData[0]);
    }
  }, [
    loanDetails,
    availableApplicants,
    selectedApplicant,
    setApplicantDataParent,
    setSelectedApplicant,
    isABFLCO_Lending,
    coApplicantDetails
  ]);

  // Compute dynamic width based on longest option text
  useEffect(() => {
    const texts = (applicantData || []).map((a) => a?.name || "");
    if (!texts.length) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const font =
      '12px Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial';

    const measure = (t: string) => {
      if (ctx) {
        try {
          ctx.font = font;
          return ctx.measureText(t).width;
        } catch {
          return t.length * 8;
        }
      }
      return t.length * 8;
    };

    const max = texts.reduce((m, t) => Math.max(m, measure(t)), 0);
    const paddingChrome = 30; // padding + icons + borders
    const desired = Math.ceil(max + paddingChrome);
    const clamped = Math.max(260, Math.min(560, desired));

    setComputedWidthPx(clamped);
  }, [applicantData]);

  useEffect(() => {
    if (resetApplicantState) {
      // setSelectedApplicant([]);

      const resetPrimaryDisplayName = isABFLCO_Lending
        ? loanDetails?.name || loanDetails?.firmName
        : loanDetails?.name;

      const primaryApplicant = {
        name: truncateNameFunc(
          `${resetPrimaryDisplayName && loanDetails?.mobile ? `${resetPrimaryDisplayName} - ${loanDetails?.mobile}` : resetPrimaryDisplayName || loanDetails?.mobile || "Unknown"} (Primary)`
        ),
        value: loanDetails?.userID || ""
      };
      setSelectedApplicant(primaryApplicant);
    }
  }, [
    resetApplicantState,
    loanDetails?.mobile,
    loanDetails?.name,
    loanDetails?.userID,
    setSelectedApplicant,
    isABFLCO_Lending
  ]);

  return (
    // <div style={{ width: computedWidthPx, minWidth: 220, maxWidth: 300 }}>
    <DropdownWithSearch
      options={applicantData || []}
      styles={{
        container: `w-full min-w-0 ${styles?.container || ""}`,
        button: "text-left min-h-[40px]",
        dropdown: "w-full min-w-0"
      }}
      disabled={disabled}
      selectedItems={selectedApplicant || undefined}
      hasSearch={hasSearch}
      placeholder={placeholder || "Select Co-Applicant"}
      onChange={(applicant: IApplicant) => {
        setSelectedApplicant(applicant);
        onChange(applicant);
      }}
      truncateName={truncateName}
    />
    // </div>
  );
};

export default ApplicantSelectionDropdown;
